import _ from 'lodash';
import { Sync } from '@material-ui/icons';
import { FirebaseError } from '@firebase/util';
import { Formik, FormikHelpers, FormikErrors } from 'formik';
import React, { Fragment, useCallback, useMemo, useRef, useState } from 'react';
import {
  setPersistence,
  signInWithEmailAndPassword,
  signInAnonymously,
  inMemoryPersistence,
  AuthErrorCodes,
} from 'firebase/auth';
import { useRouter } from 'next/router';
import { events, getAnalyticsClient } from '@/lib/analytics';
import { useActionPage, useConfig, usePromoCodeDetailsFromQuery } from '../../lib/hooks';
import { redeemPromotionCodeRequest, redeemPromotionRequest } from '../../src/helpers/promotionClient';
import { searchUserByEmailRequest } from '../../src/helpers/usersClient';
import {
  beginMfaChallenge,
  clearMfaRecaptcha,
  completeMfaChallenge,
  isMfaRequiredError,
  MFA_RECAPTCHA_WRAPPER_ID,
  MfaChallengeSession,
  MfaFactorType,
  resendPhoneMfaCode,
} from '../../src/helpers/mfaAuth';
import { RegisterViewExtendParams } from '../RegisterViewExtend';
import FormikCheckbox from '../shared/FormikCheckbox';
import FormikTextInput from '../shared/FormikTextInput';
import OTPDialog from '../RegisterForm/OTPDialog';
import { Button, StyledForm, ForgotPasswordContainer } from './LoginFormExtend.styled';
import createValidationSchema from './validations';
import { generateQaAttr, generateQaAttrFromBlok } from '../../src/helpers/qa-attribute';
import { auth } from '../../lib/firebase';
import { reportFirebaseSignInError } from '@/lib/reportFirebaseSignInError';
import { authServiceDomain } from '../../config';
import Storyblok from '../../lib/storyblok';
import {
  createConsentConditionsValidation,
  getConsentConditionsDefaultValues,
  getOmittableConsentConditionsKeys,
  ConsentConditionCheckbox,
} from '../RegisterForm/shared';
import { linkAmazonAccount, isAmazon } from 'src/helpers/amazon';

interface LoginFormExtendProps {
  id?: string;
  className?: string;
  blok: RegisterViewExtendParams & {
    terms_conditions_copy?: string;
  };
  setView: (view: string) => void;
}

type Values = {
  email: string;
  password: string;
  subscribe: boolean;
  terms: boolean;
  subscriptionCopy: string;
};

type PendingLogin = {
  values: Values;
  pxEnabled: boolean;
};

const firebaseErrors: { [key: string]: FormikErrors<Values> } = {
  [AuthErrorCodes.USER_DELETED]: { email: 'This email address does not exist.' },
  [AuthErrorCodes.INVALID_PASSWORD]: { password: 'Password does not match.' },
};

export default function LoginFormExtend(props: LoginFormExtendProps) {
  const [loading, setLoading] = useState<boolean>();
  const config = useConfig();
  const promoCodeDetails = usePromoCodeDetailsFromQuery();
  const analytics = getAnalyticsClient();

  const router = useRouter();
  const [error, setError] = useState<string | React.ReactNode>();
  const actionPage = useActionPage();

  const [mfaOpen, setMfaOpen] = useState(false);
  const [mfaFactorType, setMfaFactorType] = useState<MfaFactorType>('phone');
  const [mfaPhoneHint, setMfaPhoneHint] = useState('');
  const mfaSessionRef = useRef<MfaChallengeSession | null>(null);
  const pendingLoginRef = useRef<PendingLogin | null>(null);

  const resetError = () => {
    setError(undefined);
  };

  const onError = useCallback((message = 'Something wrong happened, please try again.', code?: string) => {
    let error: string | React.ReactNode = message.includes(':') ? message : `Error: ${message}`;

    if (message.includes('request_body')) {
      error = `Error: ${message}`;
    } else if (message.includes('INVALID_LAST_FIRST_NAME')) {
      error = 'Error: Email and password not found.';
    } else if (message.includes('UNEXPECTED_ERROR')) {
      error = 'Something wrong happened, please try again.';
    } else if (message.includes('Reached maximum redemptions by account per campaign')) {
      error =
        'We’re sorry, we’re unable to redeem this code since your account has reached the maximum number (4) of promo codes that can be redeemed for this promotion.';
    } else if (message.includes('payment.method.not.found')) {
      error = (
        <span>
          Looks like your account is not yet active.{' '}
          <a href={process.env.NEXT_PUBLIC_EVGO_LOGIN_URL} target="_blank" className="action" rel="noreferrer">
            Activate your account
          </a>{' '}
          by logging in and adding a payment method.
        </span>
      );
    }

    setError(typeof error === 'object' ? error : `${error}${code ? `| Code: ${code}` : ''}`);

    setLoading(false);
  }, []);

  const goToConfirmation = useCallback(() => {
    if (props.blok.confirmation_route && props.blok.confirmation_route.url) {
      actionPage(props.blok.confirmation_route.url, 'login');
    } else {
      actionPage('/confirmation', 'login');
    }
  }, [actionPage, props.blok.confirmation_route]);

  const initialValues: Values = {
    email: '',
    password: '',
    subscribe: true,
    terms: false,
    subscriptionCopy: props.blok.subscriptionCopy,
    ...getConsentConditionsDefaultValues(props.blok?.consent_conditions || []),
  };

  // Legacy Falcon: redeemPromotion (requires anonymous session after credential verify).
  const handleSubmit = useCallback(
    async (values: Values) => {
      try {
        const result = await redeemPromotionRequest({
          address: null,
          city: null,
          email: values.email,
          firstName: null,
          lastName: null,
          make: null,
          model: null,
          password: values.password,
          phone: null,
          postalCode: null,
          subscribe: values.subscribe,
          state: null,
          terms: values.terms,
          year: null,
          subscriptionCopy: values.subscriptionCopy,
          promotionCode: promoCodeDetails?.promotionCode,
          promotionRef: config.promotion.ref,
        });

        if (result.success) {
          goToConfirmation();
        } else {
          onError(result.message, result.errorCode);
        }
      } catch (error) {
        onError((error as Error).message);
      }
    },
    [config.promotion.ref, goToConfirmation, onError, promoCodeDetails],
  );

  // Phoenix: stay signed in as account owner → redeemPromotionCode.
  const handleSubmitPhoenix = useCallback(async () => {
    try {
      const result = await redeemPromotionCodeRequest({
        origin: 'PORTAL',
        promotionCode: promoCodeDetails?.promotionCode || '',
        promotionRef: config.promotion.ref,
      });

      if (result.id) {
        await signInAnonymously(auth);
        goToConfirmation();
      } else {
        onError('Unable to redeem promotion code, please try again.');
      }
    } catch (error) {
      onError((error as Error).message);
    }
  }, [config.promotion.ref, goToConfirmation, onError, promoCodeDetails]);

  const formRef = useRef<any>();

  const validationSchema = useMemo(
    () => createValidationSchema(createConsentConditionsValidation(props.blok?.consent_conditions || [])),
    [props.blok.consent_conditions],
  );

  const closeMfaDialog = useCallback(() => {
    setMfaOpen(false);
    setMfaPhoneHint('');
    setMfaFactorType('phone');
    mfaSessionRef.current = null;
    pendingLoginRef.current = null;
    clearMfaRecaptcha();
    setLoading(false);
  }, []);

  /**
   * After Firebase auth is complete (password-only or MFA), prepare the session for redeem:
   * Phoenix keeps the owner session; Falcon reverts to anonymous (EXT-325).
   */
  const prepareSessionForRedeem = useCallback(async (pxEnabled: boolean) => {
    if (pxEnabled) {
      // redeemPromotionCode requires isAccountOwner (accountId from Firebase claims).
      await auth.currentUser?.getIdToken(true);
    } else {
      await signInAnonymously(auth);
    }
  }, []);

  const completeLoginAfterAuth = useCallback(
    async (values: Values, pxEnabled: boolean) => {
      const omittedKeys = getOmittableConsentConditionsKeys(props.blok?.consent_conditions || []);

      if (isAmazon(router.query)) {
        try {
          await linkAmazonAccount({ email: values.email, password: values.password, query: router.query });
        } catch (error) {
          console.error('Failed to link Amazon account', { error });
          setError('Failed to link Amazon account. Please try again');
          setLoading(false);
          return;
        }
      }

      if (pxEnabled) {
        await handleSubmitPhoenix();
      } else {
        await handleSubmit(_.omit(values, omittedKeys) as Values);
      }
    },
    [handleSubmit, handleSubmitPhoenix, props.blok?.consent_conditions, router.query],
  );

  const openMfaChallenge = useCallback(async (mfaError: unknown, values: Values, pxEnabled: boolean) => {
    if (!isMfaRequiredError(mfaError)) {
      throw mfaError;
    }

    const session = await beginMfaChallenge(mfaError);
    pendingLoginRef.current = { values, pxEnabled };
    mfaSessionRef.current = session;
    setMfaFactorType(session.factorType);
    setMfaPhoneHint(session.phoneNumber || '');
    setMfaOpen(true);
  }, []);

  const handleMfaVerified = useCallback(
    async (code: string) => {
      const session = mfaSessionRef.current;
      const pending = pendingLoginRef.current;
      if (!session || !pending) {
        throw new Error('MFA session expired. Please try logging in again.');
      }

      // Invalid codes should throw so OTPDialog can show field-level feedback.
      await completeMfaChallenge(session, code);

      setMfaOpen(false);
      mfaSessionRef.current = null;
      setLoading(true);
      try {
        await prepareSessionForRedeem(pending.pxEnabled);
        await completeLoginAfterAuth(pending.values, pending.pxEnabled);
      } catch (error) {
        onError((error as Error).message || 'Something went wrong, please try again.');
      } finally {
        pendingLoginRef.current = null;
        clearMfaRecaptcha();
        setLoading(false);
      }
    },
    [completeLoginAfterAuth, onError, prepareSessionForRedeem],
  );

  const handleMfaResend = useCallback(async () => {
    const session = mfaSessionRef.current;
    if (!session || session.factorType !== 'phone') {
      throw new Error('Unable to resend MFA code.');
    }

    const verificationId = await resendPhoneMfaCode(session);
    mfaSessionRef.current = { ...session, verificationId };
  }, []);

  const handleLogin = async (values: Values, { setErrors, setStatus }: FormikHelpers<Values>) => {
    setLoading(true);
    setError(undefined);
    setStatus('');

    const { email, password } = values;

    analytics.track(events.clickLoginButton, {
      host: window.location.host,
      pathname: window.location.pathname,
      email,
    });

    try {
      let pxEnabled = false;
      try {
        const result = await searchUserByEmailRequest(email);
        pxEnabled = result.pxEnabled;
      } catch (error) {
        console.error('Phoenix user search failed, falling back to legacy flow', { error });
      }

      /**
       * Legacy Falcon redeemPromotion does not validate email/password — Firebase is the credential check.
       * Revert to anonymous after verify so Falcon redeem still satisfies isAnonymous (EXT-325).
       * MFA-enrolled accounts pause here for a second-factor challenge before that revert.
       */
      await setPersistence(auth, inMemoryPersistence);

      try {
        await signInWithEmailAndPassword(auth, email, password);
        await prepareSessionForRedeem(pxEnabled);
      } catch (signInError) {
        if (isMfaRequiredError(signInError)) {
          await openMfaChallenge(signInError, values, pxEnabled);
          return;
        }
        reportFirebaseSignInError(signInError, { 'auth.component': 'LoginFormExtend' });
        throw signInError;
      }

      await completeLoginAfterAuth(values, pxEnabled);
    } catch (e) {
      const code = (e as FirebaseError)?.code;
      const isKnownError = Object.keys(firebaseErrors).includes(code);

      if (isKnownError) {
        setErrors(firebaseErrors[code]);
        setStatus(code);
      } else {
        const message = (e as Error)?.message;
        setError(
          message?.includes('MFA') || message?.includes('reCAPTCHA')
            ? message
            : 'Something went wrong, please try again.',
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Fragment>
      <Formik
        innerRef={formRef}
        initialValues={initialValues}
        onSubmit={handleLogin}
        validationSchema={validationSchema}
        validateOnMount={true}
        {...generateQaAttrFromBlok(props.blok)}
      >
        {(formik) => (
          <StyledForm onChange={resetError}>
            <h3>{props.blok.login_copy}</h3>
            <FormikTextInput name="email" label="Email" {...generateQaAttr('email')} />
            <ForgotPasswordContainer>
              <a href={`${authServiceDomain}/reset-password`} target="_blank" rel="noreferrer">
                Forgot Password?
              </a>
              <FormikTextInput name="password" label="Password" type="password" {...generateQaAttr('password')} />
            </ForgotPasswordContainer>
            <h6>
              Minimum 8 chars: with at least 1 number, 1 uppercase, 1 lowercase
              &&nbsp;1&nbsp;special&nbsp;character&nbsp;(!,&nbsp;@,&nbsp;#&nbsp;...)
            </h6>
            <hr />
            {props.blok.terms_conditions_copy ? (
              <FormikCheckbox name="terms" {...generateQaAttr('terms-checkbox')}>
                <div dangerouslySetInnerHTML={{ __html: props.blok.terms_conditions_copy }} />
              </FormikCheckbox>
            ) : (
              <FormikCheckbox name="terms" {...generateQaAttr('terms-checkbox')}>
                By checking this box, you acknowledge you have read and agree to be bound by our{' '}
                <a target="_blank" rel="noreferrer" href="https://www.evgo.com/terms-service/" className="underline">
                  Terms of Service
                </a>{' '}
                and our{' '}
                <a target="_blank" rel="noreferrer" href="https://www.evgo.com/privacy-policy/" className="underline">
                  Privacy Policy
                </a>
                . By submitting this form, you acknowledge you are sharing your personal information with EVgo and
                consent to EVgo’s Privacy Notice for California Residents. More detail regarding personal information we
                collect, how we use that information, how we share that information, and your rights and choices can be
                found in our{' '}
                <a target="_blank" rel="noreferrer" href="https://www.evgo.com/privacy-policy/" className="underline">
                  Privacy Policy
                </a>
                .
              </FormikCheckbox>
            )}
            <FormikCheckbox name="subscribe" {...generateQaAttr('subscribe-checkbox')}>
              {props.blok.subscriptionCopy}
            </FormikCheckbox>

            {props.blok?.consent_conditions
              ?.filter((v) => !!v.id)
              ?.map((condition) => (
                <ConsentConditionCheckbox
                  key={condition._uid}
                  name={condition.id}
                  {...generateQaAttr(`${condition.id}-checkbox`)}
                  wrapLabel={false}
                >
                  <span
                    className="condition"
                    dangerouslySetInnerHTML={{ __html: Storyblok.richTextResolver.render(condition.description) }}
                  />
                </ConsentConditionCheckbox>
              ))}

            <div className="submit-container">
              <Button type="submit" disabled={!!loading || !formik.isValid || mfaOpen}>
                <span className={loading ? 'loading' : ''}>{props.blok.login_submit_button_copy}</span>
                {loading ? <Sync className="spinner" /> : null}
              </Button>

              {formik.status === AuthErrorCodes.USER_DELETED && (
                <p className="error-container">
                  This email address does not exist. Please try again, or{' '}
                  <span className="action" onClick={() => props.setView('register')}>
                    register for an EVgo account
                  </span>
                  .
                </p>
              )}

              {formik.status === AuthErrorCodes.INVALID_PASSWORD && (
                <p className="error-container">Password does not match. Please try again.</p>
              )}

              {error ? <p className="error-container">{error}</p> : <p>{props.blok.login_submit_button_subtext}</p>}
            </div>
          </StyledForm>
        )}
      </Formik>

      <div id={MFA_RECAPTCHA_WRAPPER_ID} />

      <OTPDialog
        open={mfaOpen}
        phoneNumber={mfaPhoneHint}
        showResend={mfaFactorType === 'phone'}
        description={
          mfaFactorType === 'totp'
            ? 'Enter the 6-digit code from your authenticator app to finish signing in.'
            : undefined
        }
        onVerified={handleMfaVerified}
        onResend={mfaFactorType === 'phone' ? handleMfaResend : undefined}
        onCancel={closeMfaDialog}
      />
    </Fragment>
  );
}
